/**
 * The tech's call in progress (active calls, AC-3; ADRs 0047 and 0048): who
 * it's with, the client the number matches, and choosing the client while the
 * call happens. The choice is kept by the call, and its record gets it when it
 * arrives. A small card in the corner of the day view, under any call still
 * ringing (Idea #86; DayPage's call corner): nothing else moves.
 *
 * Checked every few seconds while the tab is visible.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { ApiError } from "../../api/client";
import type { ActiveCall, ClientChoice } from "../../api/types";
import { useSession } from "../../app/session-context";
import { ClientCombobox } from "../../components/ui/ClientCombobox";
import { formatPhone } from "../../lib/phones";

const POLL_MS = 5_000;

function refusal(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 409) {
      return "That number is already another client's. Choose the client without remembering it.";
    }
    if (error.status === 404) {
      return "This call has ended.";
    }
    return error.fieldErrors[0]?.message ?? error.detail ?? error.message;
  }
  return error instanceof Error ? error.message : "Something went wrong.";
}

function ChooseForm({
  call,
  onChosen,
}: {
  call: ActiveCall;
  onChosen: () => void;
}) {
  const { api } = useSession();
  const queryClient = useQueryClient();
  const [clientId, setClientId] = useState(
    call.chosen?.client.id ?? call.client?.id ?? "",
  );
  const [remember, setRemember] = useState(false);
  const choices = useQuery({
    queryKey: ["client-choices"],
    queryFn: () => api.listClientChoices(),
    staleTime: 5 * 60_000,
    retry: 1,
  });
  const chosen = choices.data?.find((c) => c.id === clientId);
  const number = call.number === null ? null : formatPhone(call.number);
  const choose = useMutation({
    mutationFn: (client: ClientChoice) =>
      api.chooseCallClient(
        call.call_id,
        client.id,
        remember && number !== null,
      ),
    onSuccess: async () => {
      onChosen();
      await queryClient.invalidateQueries({ queryKey: ["active-call"] });
    },
  });

  return (
    <form
      aria-label="Choose the call's client"
      className="flex flex-col gap-2.5"
      onSubmit={(event) => {
        event.preventDefault();
        if (chosen) {
          choose.mutate(chosen);
        }
      }}
    >
      <ClientCombobox
        label="Client"
        choices={choices.data}
        failed={choices.isError}
        value={clientId}
        onChange={setClientId}
      />
      {number !== null ? (
        <label className="flex items-center gap-2 text-[13px]">
          <input
            type="checkbox"
            checked={remember}
            onChange={(event) => {
              setRemember(event.target.checked);
            }}
            className="size-4 accent-brand"
          />
          {`Remember ${number} for ${chosen?.name ?? "this client"}`}
        </label>
      ) : null}
      {choose.isError ? (
        <p role="alert" className="text-[13px] text-status-needs-client-fg">
          {refusal(choose.error)}
        </p>
      ) : null}
      <button
        type="submit"
        disabled={chosen === undefined || choose.isPending}
        className="h-10 cursor-pointer rounded-pill bg-cta-bg px-4 text-[13.5px] font-bold text-cta-fg disabled:cursor-not-allowed disabled:opacity-50"
      >
        {choose.isPending
          ? "Choosing…"
          : chosen
            ? `Choose ${chosen.name}`
            : "Choose client"}
      </button>
    </form>
  );
}

export function ActiveCallPopup() {
  const { api } = useSession();
  const [hidden, setHidden] = useState<string | null>(null);
  const [changing, setChanging] = useState<string | null>(null);
  const query = useQuery({
    queryKey: ["active-call"],
    queryFn: () => api.getActiveCall(),
    refetchInterval: POLL_MS,
    // Only while the tab is visible.
    refetchIntervalInBackground: false,
    retry: false,
  });
  const call = query.data ?? null;
  // On the page before any call, so a screen reader announces the call when
  // it arrives; once a call, not as the tech works in the form (#249).
  const status = (
    <p data-part="call-status" role="status" className="sr-only">
      {call === null
        ? ""
        : `Live call ${call.direction === "inbound" ? "with" : "to"} ${call.number === null ? "an unknown number" : formatPhone(call.number)}`}
    </p>
  );
  if (call === null || hidden === call.call_id) {
    return status;
  }
  const number =
    call.number === null ? "an unknown number" : formatPhone(call.number);
  const showForm = call.chosen === null || changing === call.call_id;

  return (
    <>
      {status}
      <section
        role="region"
        aria-label="Your call"
        className="flex flex-col overflow-hidden rounded-panel border border-brand bg-panel shadow-[0_14px_30px_var(--brand-shadow)]"
      >
        {/* The design's splash of Axon blue (section 11.0), like the hero tile:
          a call in progress stands out from the white panels behind it. */}
        <div
          data-part="band"
          className="flex items-start justify-between gap-3 bg-brand bg-[radial-gradient(90%_130%_at_100%_0%,var(--hero-glow),transparent_60%)] px-4 py-3.5 text-white"
        >
          <div className="min-w-0">
            <p className="flex items-center gap-1.5 text-[11.5px] font-semibold tracking-wide uppercase">
              <span
                aria-hidden="true"
                className="size-2 rounded-pill bg-white motion-safe:animate-pulse"
              />
              Live call
            </p>
            <h2 className="mt-0.5 text-[16px] font-bold tracking-tight">
              {call.direction === "inbound"
                ? `On a call with ${number}`
                : `On a call to ${number}`}
            </h2>
            <p className="text-[13px] text-white/90">
              {call.client
                ? `Matches ${call.client.name}`
                : "No client matches this number"}
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              setHidden(call.call_id);
            }}
            className="h-8 shrink-0 cursor-pointer rounded-pill border border-white/50 bg-white/10 px-3 text-[12.5px] font-medium text-white hover:bg-white/20 focus-visible:outline-white"
          >
            Hide
          </button>
        </div>
        <div className="flex flex-col gap-3 p-4">
          {showForm ? (
            <ChooseForm
              key={call.call_id}
              call={call}
              onChosen={() => {
                setChanging(null);
              }}
            />
          ) : (
            <div className="flex items-center justify-between gap-3 rounded-row bg-panel-2 px-3 py-2">
              <p className="text-[13.5px] font-semibold">
                {`Client: ${call.chosen?.client.name ?? ""}${call.chosen?.remembered ? " · number remembered" : ""}`}
              </p>
              <button
                type="button"
                onClick={() => {
                  setChanging(call.call_id);
                }}
                className="cursor-pointer text-[12.5px] font-semibold text-brand-ink hover:underline"
              >
                Change
              </button>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
